import type { RefObject } from "react";
import { buildHref } from "@softmax/web-addresses";
import type { CogentLive } from "./chat-view";
import type { WidgetActions } from "../use-widget-actions";
import type { CogentPresence, CogentWidgetProps } from "../widget-types";
import { WidgetConversation } from "./widget-conversation";
import { WidgetParticipation } from "./widget-participation";
import { WidgetPlace } from "./widget-place";
import { WidgetLiveStatus } from "../notices/widget-live-status";
import s from "../cogent.module.css";
import controls from "./widget-chat.module.css";

type WidgetChatProps = {
  ids: string;
  open: boolean;
  name: string;
  state: CogentPresence;
  props: CogentWidgetProps;
  live: CogentLive | null;
  actions: WidgetActions;
  panel: RefObject<HTMLDialogElement | null>;
  log: RefObject<HTMLDivElement | null>;
  close: () => void;
};

type HeaderProps = Pick<WidgetChatProps, "ids" | "state" | "props" | "close">;

function WidgetChatHeader({ ids, state, props, close }: HeaderProps) {
  const { cogent } = props;

  return (
    <div className={s.head}>
      <div className={s.titles}>
        <div id={`${ids}-name`} className={s.name} title={cogent?.name}>
          {cogent ? cogent.name : "Your cogent"}
          <span className={s.srOnly}>, {state}</span>
        </div>
      </div>
      <button
        type="button"
        className={s.icon}
        aria-label="Close chat"
        title="Close chat"
        onClick={close}
      >
        <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
          <path
            d="M4 4l8 8M12 4l-8 8"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
}

export function WidgetChat({
  ids,
  open,
  name,
  state,
  props,
  live,
  actions,
  panel,
  log,
  close,
}: WidgetChatProps) {
  const { cogent, coworld } = props;

  return (
    <dialog
      ref={panel}
      id={`${ids}-box`}
      className={`${s.bubble} ${controls.dialog}`}
      aria-labelledby={`${ids}-name`}
      tabIndex={-1}
      open={open}
      hidden={!open}
      data-testid="cogent-box"
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <span className={s.tail} aria-hidden="true" />
      <div className={s.body}>
        <WidgetChatHeader ids={ids} state={state} props={props} close={close} />
        {props.unavailable ? (
          <>
            <p className={s.line}>{props.unavailable}</p>
            <textarea
              aria-label="Message your cogent"
              placeholder="Message your cogent"
              disabled
            />
          </>
        ) : cogent ? (
          <>
            <WidgetPlace cogent={cogent} coworld={coworld} />
            <WidgetConversation
              name={name}
              cogent={cogent}
              start={props.start}
              live={live}
              actions={actions}
              log={log}
            />
            <WidgetLiveStatus live={live} />
            <WidgetParticipation name={name} props={props} actions={actions} />
          </>
        ) : props.signedIn ? null : (
          <p className={s.line} data-testid="cogent-none">
            Sign in to get your own cogent.{" "}
            <a
              className={s.link}
              href={props.signInHref ?? buildHref({ name: "signIn" })}
              data-testid="cogent-sign-in"
            >
              Sign in
            </a>
          </p>
        )}
        <output
          className={s.status}
          role={actions.status.kind === "error" ? "alert" : undefined}
          data-kind={actions.status.kind}
          data-testid="cogent-status"
        >
          {actions.status.kind === "idle" ? "" : actions.status.text}
        </output>
      </div>
    </dialog>
  );
}
